import {
  SUPER_ADMIN_AUDIT_ENTITY_TYPES,
  isSuperAdminAuditActionType,
  type SuperAdminAuditDetail,
} from "@multi-chat/shared";
import type { TFunction } from "i18next";

const ROLE_KEYS = new Set(["role", "previousRole", "invitedRole"]);
const ROLE_VALUES = new Set(["OWNER", "ADMIN", "AGENT"]);
const ORGANIZATION_STATUS_KEYS = new Set(["statusBefore", "statusAfter"]);
const ORGANIZATION_STATUS_VALUES = new Set(["ACTIVE", "SUSPENDED"]);
const PLATFORM_VALUES = new Set(["WHATSAPP", "FACEBOOK", "INSTAGRAM"]);
const CONTACT_IMPORT_STATUS_VALUES = new Set(["COMPLETED", "CANCELLED", "FAILED"]);
const DATE_KEYS = new Set(["scheduledAt", "requestedAt", "completedAt"]);
const ENTITY_TYPES = new Set<string>(SUPER_ADMIN_AUDIT_ENTITY_TYPES);

/** Localized action label. Unknown actions never surface their raw code. */
export function auditActionLabel(t: TFunction, actionType: string): string {
  return isSuperAdminAuditActionType(actionType)
    ? t(`superAdmin.logs.actions.${actionType}`)
    : t("superAdmin.logs.actions.unknown");
}

export function auditEntityLabel(t: TFunction, entityType: string): string {
  return ENTITY_TYPES.has(entityType)
    ? t(`superAdmin.logs.entities.${entityType}`)
    : t("superAdmin.logs.entities.unknown");
}

export function formatAuditTime(value: string, language: string): string {
  return new Date(value).toLocaleString(language, { dateStyle: "medium", timeStyle: "medium" });
}

/**
 * Display text for one allowlisted detail. Known enums are localized; other
 * strings are shown as recorded (names, file names, identifiers).
 */
export function formatAuditDetailValue(
  t: TFunction,
  language: string,
  detail: SuperAdminAuditDetail,
): string {
  const { key, value } = detail;
  if (value === null) return t("superAdmin.logs.values.empty");
  if (typeof value === "boolean") {
    return value ? t("superAdmin.logs.values.yes") : t("superAdmin.logs.values.no");
  }
  if (typeof value === "number") {
    return /Ids?$/.test(key) ? String(value) : new Intl.NumberFormat(language).format(value);
  }
  if (ROLE_KEYS.has(key) && ROLE_VALUES.has(value)) {
    return t(`organization.role.${value.toLowerCase()}`);
  }
  if (ORGANIZATION_STATUS_KEYS.has(key) && ORGANIZATION_STATUS_VALUES.has(value)) {
    return t(`superAdmin.status.${value}`);
  }
  if (key === "platformType" && PLATFORM_VALUES.has(value)) {
    return t(`superAdmin.platforms.${value.toLowerCase()}`);
  }
  if (key === "importStatus" && CONTACT_IMPORT_STATUS_VALUES.has(value)) {
    return t(`contacts.import.job.status.${value}`);
  }
  if (DATE_KEYS.has(key) && !Number.isNaN(Date.parse(value))) {
    return formatAuditTime(value, language);
  }
  return value;
}

/** `YYYY-MM-DD` from a date input, as the start of that local day in ISO form. */
export function localDayStartIso(day: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const date = new Date(`${day}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** Exclusive upper bound: the start of the local day after `day`. */
export function localDayEndExclusiveIso(day: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const date = new Date(`${day}T00:00:00`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}
